(ns practitest.frontend.components2.dashboard.charts.marks-bar
  (:require ["d3" :as d3]))

(defn- animation-defaults [mark]
  (merge {:ease-exit       (.-easeCubicIn d3)
          :ease-update     (.-easeCubicInOut d3)
          :ease-enter      (.-easeCubicOut d3)
          :exit-duration   200
          :update-duration 300
          :enter-duration  200}
         (:animation mark)))

(defn- tooltip-rows-for-cat [cat-rows color-scale]
  (->> cat-rows
       (filter #(pos? (:value %)))
       (mapv (fn [{:keys [group value]}]
               {:label group :value value :color (color-scale group)}))))

(defn- exit-fade! [sel {:keys [exit-duration ease-exit]}]
  (-> (.exit sel)
      (.transition) (.duration exit-duration) (.ease ease-exit)
      (.style "opacity" 0)
      (.remove)))

(defn- exit-collapse-bars! [sel {:keys [exit-duration ease-exit]} baseline-y]
  (-> (.exit sel)
      (.transition) (.duration exit-duration) (.ease ease-exit)
      (.attr "y" baseline-y)
      (.attr "height" 0)
      (.remove)))

(defn- enter-bars! [sel {:keys [exit-duration update-duration enter-duration ease-enter]} y-fn h-fn]
  (-> sel
      (.transition) (.delay (+ exit-duration update-duration))
      (.duration enter-duration) (.ease ease-enter)
      (.attr "y" y-fn)
      (.attr "height" h-fn)))

(defn- value-tooltip-row [cat ^js d]
  {:label   cat
   :value   (- (aget d 1) (aget d 0))
   :marker? false})

(defn- tooltip-anchor [x-scale y-scale ^js d]
  {:anchor-x (+ (x-scale (.. d -data -category))
               (/ (.bandwidth x-scale) 2))
   :anchor-y (+ (y-scale (aget d 1)) 8)})

(defn- tooltip-rows [mode tooltip-by-cat ^js d]
  (let [cat (.. d -data -category)]
    (case mode
      :category-value [(value-tooltip-row cat d)]
      :category-group (get tooltip-by-cat cat [])
      [])))

(defn- tooltip-payload [mode tooltip-by-cat x-scale y-scale ^js d]
  (merge {:category (when (= mode :category-group)
                      (.. d -data -category))
          :rows     (tooltip-rows mode tooltip-by-cat d)}
         (tooltip-anchor x-scale y-scale d)))

(defn- render-stacked-layer!
  [this-node x-scale y-scale baseline-y anim tooltip-by-cat tooltip tooltip-mode ^js layer]
  (let [{:keys [exit-duration update-duration ease-update]} anim
        g          (.select d3 this-node)
        cat-key    (fn [^js d] (.. d -data -category))
        rects      (-> g
                       (.selectAll "rect.stack-rect")
                       (.data layer cat-key))
        new-rects  (-> (.enter rects)
                       (.append "rect")
                       (.attr "class" "stack-rect")
                       (.attr "x" (fn [^js d] (x-scale (.. d -data -category))))
                       (.attr "width" (.bandwidth x-scale))
                       (.attr "y" baseline-y)
                       (.attr "height" 0))
        y-top      (fn [^js d] (y-scale (aget d 1)))
        y-height   (fn [^js d] (max 0 (- (y-scale (aget d 0)) (y-scale (aget d 1)))))]
    (exit-collapse-bars! rects anim baseline-y)

    (-> rects
        (.transition) (.delay exit-duration) (.duration update-duration) (.ease ease-update)
        (.attr "x" (fn [^js d] (x-scale (.. d -data -category))))
        (.attr "width" (.bandwidth x-scale))
        (.attr "y" y-top)
        (.attr "height" y-height))

    (enter-bars! new-rects anim y-top y-height)

    (when-let [{:keys [show! hide!]} tooltip]
      (let [all-rects (.merge new-rects rects)]
        (-> all-rects
            (.on "mouseenter"
                 (fn [^js _event ^js d]
                   (show! (tooltip-payload tooltip-mode
                                           tooltip-by-cat
                                           x-scale
                                           y-scale
                                           d))))
            (.on "mouseleave"
                 (fn [^js _e ^js _d] (hide!))))))))

(defn render-stacked!
  [svg mark {:keys [data encode tooltip tooltip-spec]}]
  (let [marks-g        (.select svg "g.chart g.marks")
        x-scale        (get-in encode [:x :d3-scale])
        y-scale        (get-in encode [:y :d3-scale])
        color-scale    (get-in encode [:color :d3-scale])
        anim           (animation-defaults mark)
        baseline-y     (y-scale 0)
        categories     (get-in encode [:x :scale :domain])
        series-labels  (->> data (map :group) distinct)
        by-category    (group-by :category data)
        tooltip-mode   (get tooltip-spec :mode :category)
        tooltip-by-cat (when tooltip
                         (into {}
                               (map (fn [[cat rows]]
                                      [cat (tooltip-rows-for-cat rows color-scale)]))
                               by-category))
        row-data       (clj->js
                        (mapv (fn [cat]
                                (let [rows-for-cat (get by-category cat [])
                                      base-row     {"category" (str cat)}]
                                  (reduce (fn [obj {:keys [group value]}]
                                            (assoc obj (str group) value))
                                          base-row
                                          rows-for-cat)))
                              categories))
        stack          (-> (.stack d3) (.keys (into-array (map str series-labels))))
        layers         (stack row-data)
        series-sel     (-> marks-g
                           (.selectAll "g.stack-layer")
                           (.data layers (fn [^js layer] (.-key layer))))
        entered        (-> (.enter series-sel)
                           (.append "g")
                           (.attr "class" "stack-layer"))]

    (exit-fade! series-sel anim)

    (let [all-layers (.merge entered series-sel)]
      (.attr all-layers "fill" (fn [^js layer] (color-scale (.-key layer))))
      (.each all-layers
             (fn [^js layer]
               (this-as this
                 (render-stacked-layer! this
                                        x-scale
                                        y-scale
                                        baseline-y
                                        anim
                                        tooltip-by-cat
                                        tooltip
                                        tooltip-mode
                                        layer)))))))

(defn render-bar! [svg mark ctx]
  ;; TODO:
  ;; `render-bar!` will be implemented as part of the bar chart migration
  ;; to the new Vega-Lite–like architecture.
  nil)